import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Layout } from '@/components/Layout'
import { Login } from '@/components/Login'
import { AuthCallback } from '@/pages/AuthCallback'
import { Projects } from '@/pages/Projects'
import { ProjectsMap } from '@/pages/ProjectsMap'
import { CreateProject } from '@/pages/CreateProject'
import { Units } from '@/pages/Units'
import { UnitDetail } from '@/pages/UnitDetail'
import { UnitAnalytics } from '@/pages/UnitAnalytics'
import { ProjectDetail } from '@/pages/ProjectDetail'
import { Architects } from '@/pages/Architects'
import { ArchitectDetail } from '@/pages/ArchitectDetail'
import { Developers } from '@/pages/Developers'
import { DeveloperDetail } from '@/pages/DeveloperDetail'
import { Brokers } from '@/pages/Brokers'
import { BrokerDetail } from '@/pages/BrokerDetail'
import { Monitoring } from '@/pages/Monitoring'
import { Discovery } from '@/pages/Discovery'
import { AnalyticsOverview } from '@/pages/analytics/AnalyticsOverview'
import { MarketAnalytics } from '@/pages/analytics/MarketAnalytics'
import { PipelineAnalytics } from '@/pages/analytics/PipelineAnalytics'
import { DeveloperScorecards } from '@/pages/analytics/DeveloperScorecards'
import { BrokerScorecards } from '@/pages/analytics/BrokerScorecards'
import { ArchitectScorecards } from '@/pages/analytics/ArchitectScorecards'
import { Comparables } from '@/pages/analytics/Comparables'
import { AuditOptimized } from '@/pages/AuditOptimized'
import { UserManagement } from '@/pages/UserManagement'
import { McpAccess } from '@/pages/admin/McpAccess'
import { useAuth } from '@/hooks/useAuth'

function ProtectedRoute({
  children,
  requireAdmin = false,
}: {
  children: React.ReactNode
  requireAdmin?: boolean
}) {
  const { isAuthenticated, isAdmin, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (requireAdmin && !isAdmin) {
    // Analysts who try to deep-link to an admin-only URL get bounced to the map.
    return <Navigate to="/projects-map" replace />
  }

  return <>{children}</>
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/*" element={
        <ProtectedRoute>
          <Layout>
            <Routes>
              <Route path="/" element={<Navigate to="/projects-map" replace />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects-map" element={<ProjectsMap />} />
              <Route
                path="/projects/create"
                element={
                  <ProtectedRoute requireAdmin>
                    <CreateProject />
                  </ProtectedRoute>
                }
              />
              <Route path="/projects/:id" element={<ProjectDetail />} />
              <Route path="/units" element={<Units />} />
              <Route path="/units/:id" element={<UnitDetail />} />
              <Route path="/units/:unitId/analytics" element={<UnitAnalytics />} />
              <Route path="/architects" element={<Architects />} />
              <Route path="/architects/:id" element={<ArchitectDetail />} />
              <Route path="/developers" element={<Developers />} />
              <Route path="/developers/:id" element={<DeveloperDetail />} />
              <Route path="/brokers" element={<Brokers />} />
              <Route path="/brokers/:id" element={<BrokerDetail />} />
              <Route
                path="/analytics"
                element={
                  <ProtectedRoute requireAdmin>
                    <AnalyticsOverview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics/market"
                element={
                  <ProtectedRoute requireAdmin>
                    <MarketAnalytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics/pipeline"
                element={
                  <ProtectedRoute requireAdmin>
                    <PipelineAnalytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics/developers"
                element={
                  <ProtectedRoute requireAdmin>
                    <DeveloperScorecards />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics/brokers"
                element={
                  <ProtectedRoute requireAdmin>
                    <BrokerScorecards />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics/architects"
                element={
                  <ProtectedRoute requireAdmin>
                    <ArchitectScorecards />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics/comparables"
                element={
                  <ProtectedRoute requireAdmin>
                    <Comparables />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/discovery"
                element={
                  <ProtectedRoute requireAdmin>
                    <Discovery />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/monitoring"
                element={
                  <ProtectedRoute requireAdmin>
                    <Monitoring />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/audit"
                element={
                  <ProtectedRoute requireAdmin>
                    <AuditOptimized />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute requireAdmin>
                    <UserManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/mcp"
                element={
                  <ProtectedRoute requireAdmin>
                    <McpAccess />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Layout>
        </ProtectedRoute>
      } />
    </Routes>
  )
}

export default App
